<template>
  <BoardView :pagination="pagination" :title="$t('title')" :total="total">
    <Route
      slot="button"
      class="Btn withIcon icon-side-left info"
      to="ArticleTableAdd">
      <Icon name="plus" /> {{ $t('add') }}
    </Route>
    <BoardTable
      :hasResults="!!total"
      :isFetching="isFetching"
      :pagination="pagination">
      <ArticleActions
        v-if="results.length"
        :idsAll="articleIds"
        :idsSelected.sync="idsSelected" />
      <ArticleTable
        :articles="results"
        :idsSelected.sync="idsSelected" />
    </BoardTable>
    <router-view />
  </BoardView>
</template>

<script>
import { actions, getters } from '@utils'
import { ARTICLE_TYPES } from '@store/modules/article'
import ArticleActions from './components/ArticleActions'
import ArticleTable from './components/ArticleTable'
import Meta from '@mixins/Meta'
import Table from '@mixins/Table'
import { difference } from 'rambda'
import { pluckId } from '@utils/collections'

export default {
  mixins: [
    Meta('Article Table'),
    Table,
  ],
  components: {
    ArticleActions,
    ArticleTable,
  },
  data() {
    return {
      hasFetchedSectionTemplate: false,
      idsSelected: [],
      paginationOptions: {
        name: 'articleTable',
        service: 'article',
        searchIn: [
          'name',
          'title',
          'slug',
        ],
        perPage: 24,
        filters: [
          {
            label: 'Type',
            type: 'select',
            append: 'type',
            options: () => this.articleTypeOptions,
          },
          {
            label: 'Sites',
            type: 'multiselect',
            optionLabel: 'name',
            trackBy: 'id',
            append: value => (value ? {
              siteId: {
                $in: pluckId('id', value),
              },
            } : {}),
            options: () => this.query('site'),
          },
        ],
        filtersValues: {
          Type: ARTICLE_TYPES.BUYING_GUIDE,
        },
      },
    }
  },
  computed: {
    articleIds() {
      return pluckId('id', this.results)
    },
    ...getters('articleTypeOptions'),
  },
  methods: {
    ...actions(
      'FETCH_ARTICLE',
      'FETCH_ARTICLE_BLUEPRINT',
      'FETCH_ARTICLE_LAPTOP',
      'FETCH_FACT',
      'FETCH_FILE',
      'FETCH_MISSING_ARTICLE',
      'FETCH_SECTION',
      'FETCH_SECTION_TEMPLATE',
    ),
  },
  watch: {
    async articleIds(articleIds, previous) {
      const articleIdsNew = difference(articleIds, previous || [])

      if (!articleIdsNew.length) return

      const idIn = (key, value) => ({ [key]: { $in: value } })

      const articleIdsQuery = idIn('articleId', articleIdsNew)
      const articlesNew = this.query('article', idIn('id', articleIdsNew))
      const archArticleIds = difference(
        pluckId('archArticleId', articlesNew),
        articleIds,
      )

      await Promise.all([
        this.FETCH_MISSING_ARTICLE(archArticleIds),
        this.FETCH_ARTICLE_BLUEPRINT(articleIdsQuery),
        this.FETCH_ARTICLE_LAPTOP(articleIdsQuery),
        this.FETCH_FACT(articleIdsQuery),
        this.FETCH_SECTION(articleIdsQuery),
      ])

      const sections = this.query('section', articleIdsQuery)
      const imageFileIds = pluckId('imageFileId', [...articlesNew, ...sections])

      return Promise.all([
        this.FETCH_FILE(imageFileIds),
        ...(() => {
          if (this.hasFetchedSectionTemplate) return []

          this.hasFetchedSectionTemplate = true

          return [this.FETCH_SECTION_TEMPLATE()]
        })(),
      ])
    },
  },
}
</script>

<style lang="scss">
.PrefillBatch {
  margin-bottom: $grid-unit;
}
</style>

<i18n>
{
  "en": {
    "title": "Prefill batch",
    "add": "Add new batch",
    "title": "Articles"
  }
}
</i18n>
